import { TriangleAlertIcon } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import { EventProperties } from '@/lib/analytics/events'
import { useUrlParams } from '@/lib/url/url-param.hook'

import { AlertStatusBadge } from '@/components/shared/alert-status-badge/alert-status-badge'
import { MenuItem } from '@/components/shared/submit-select/types'

// Generic status selector configuration
export interface StatusConfig<TStatus extends number> {
  statusEnum: Record<string, TStatus>
  statusLabels: Record<TStatus, string>
  getStatusIcon: (status: TStatus) => ReactNode
  getMenuItems: (
    currentStatus: TStatus,
    createBadge: (status: TStatus) => ReactNode,
    isFalsePositive: boolean,
  ) => MenuItem<TStatus>[]
  isStatusReadOnly: (status: TStatus) => boolean
}

export interface StatusSelectorDependencies<TStatus extends number> {
  updateMutation: {
    mutate: (variables: { id: string; status: TStatus; comment?: string }) => void
  }
  trackingCallback: (properties?: EventProperties) => Promise<void>
  entityId: string
  config: StatusConfig<TStatus>
}

export interface UseStatusSelectorProps<TStatus extends number> {
  status: TStatus
  dependencies: StatusSelectorDependencies<TStatus>
  extraTrackingProps?: EventProperties
  readOnly?: boolean
}

export interface UseStatusSelectorReturn<TStatus extends number> {
  statusBadge: ReactNode
  isReadOnly: boolean
  menuItems: MenuItem<TStatus>[]
  handleSave: (value: TStatus, comment: string) => void
  handleSendComment: (comment: string) => void
  comment: string
  isFalsePositive: boolean
}

export const useStatusSelector = <TStatus extends number>({
  status,
  dependencies,
  extraTrackingProps,
  readOnly = false,
}: UseStatusSelectorProps<TStatus>): UseStatusSelectorReturn<TStatus> => {
  const { updateMutation, trackingCallback, entityId, config } = dependencies
  const { urlParams } = useUrlParams()
  const [comment, setComment] = useState('')
  const [isFalsePositive, setIsFalsePositive] = useState(false)

  useEffect(() => {
    const keywords = ['false', 'positive', 'wrong']
    if (!comment.trim()) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsFalsePositive(false)
      return
    }
    const commentLower = comment.toLowerCase()
    const isFalsePositive = keywords.some((word) => commentLower.includes(word))
    // oxlint-disable-next-line react/set-state-in-effect
    setIsFalsePositive(isFalsePositive)
  }, [comment])

  // Create status badge component
  const createStatusBadge = useCallback(
    (badgeStatus: TStatus): ReactNode => <AlertStatusBadge status={badgeStatus} />,
    [],
  )

  // Status badge for display
  const statusBadge = createStatusBadge(status)

  // Handle save
  const handleSave = useCallback(
    (value: TStatus, comment: string) => {
      trackingCallback({
        tab: urlParams.companyDrawerTab,
        entityId,
        comment,
        status: config.statusLabels[value],
        previousStatus: config.statusLabels[status],
        isFalsePositive: isFalsePositive,
        ...extraTrackingProps,
      })
      updateMutation.mutate({ id: entityId, status: value, comment: comment || undefined })
    },
    [
      trackingCallback,
      urlParams.companyDrawerTab,
      entityId,
      config.statusLabels,
      status,
      isFalsePositive,
      extraTrackingProps,
      updateMutation,
    ],
  )

  // Handle comment change
  const handleSendComment = useCallback((comment: string) => {
    setComment(comment)
  }, [])

  // Generate warning message renderer
  const warningMessageRenderer = useCallback(
    (message: ReactNode) => (
      <div className='flex max-w-56 items-start gap-2'>
        <TriangleAlertIcon size={12} className='mt-0.5 shrink-0 text-yellow-500' />
        <span className='inline-block break-words text-md leading-tight text-yellow-500'>
          {message}
        </span>
      </div>
    ),
    [],
  )

  // Check if component should be read-only
  const isReadOnly = readOnly || config.isStatusReadOnly(status)

  // Generate menu items
  const menuItems = config.getMenuItems(status, createStatusBadge, isFalsePositive).map((item) => {
    if (item.type === 'menuSelectItem' && item.warningMessage && !item.warningMessageRenderer) {
      return {
        ...item,
        warningMessageRenderer,
      }
    }
    return item
  })

  return {
    statusBadge,
    isReadOnly,
    menuItems,
    handleSave,
    handleSendComment,
    // Expose these for any custom usage
    comment,
    isFalsePositive,
  }
}
